import React from 'react';
import styled from 'styled-components';
import ProfileImage from '../ProfileImage';
import VipIcon from '../../Icons/VipIcons';

/**
 * SelectableUserCard - A user card with checkbox for selection (lists, messaging)
 * Used in AddUsersModal, NewMessage recipient selection, etc.
 */

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: ${({ theme, $selected }) => 
    $selected ? theme.colors.primaryGradientLight : theme.colors.bg2};
  border: 1px solid ${({ theme, $selected }) => 
    $selected ? theme.colors.primaryPink : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryGradientLight};
    border-color: ${({ theme }) => theme.colors.primaryPink}40;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FullName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VerifiedBadge = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primaryPink};
  flex-shrink: 0;
`;

const Username = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $selected }) => 
    $selected ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  color: white;
`;

const SelectableUserCard = ({
  user,
  selected = false,
  onSelect,
  disabled = false,
  showCheckbox = true,
  className,
}) => {
  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(user);
    }
  };

  return (
    <CardContainer 
      onClick={handleClick} 
      $selected={selected}
      className={className}
      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <ProfileImage user={user} size="40px" />
      
      <UserInfo>
        <NameRow>
          <FullName>{user.fullName || user.displayName || user.username}</FullName>
          {user.verified && (
            <VerifiedBadge>
              <VipIcon name="VerifiedBadge" size={12} />
            </VerifiedBadge>
          )}
        </NameRow>
        <Username>@{user.username}</Username>
      </UserInfo>

      {showCheckbox && (
        <Checkbox $selected={selected}>
          {selected && <VipIcon name="Checkmark" size={12} />}
        </Checkbox>
      )}
    </CardContainer>
  );
};

export default SelectableUserCard;
